import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaSearch, FaDumbbell, FaTimes } from 'react-icons/fa';
import './ExerciseSearchPicker.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const getYouTubeThumbUrl = (url) => {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
  );
  return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
};

/**
 * Self-contained exercise search/browse picker rendered as a modal overlay.
 * Searches the paginated exercise catalog and calls onSelect with the chosen
 * raw exercise document. Used to add or swap an exercise mid-workout.
 */
const ExerciseSearchPicker = ({ title = 'Add Exercise', onSelect, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/fitness/exercises-paginated?search=${encodeURIComponent(q)}&limit=20`
      );
      if (res.ok) {
        const data = await res.json();
        setResults(data.exercises || data);
      }
    } catch (err) {
      console.error('Exercise search failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(timerRef.current);
  }, [query, search]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="exercise-picker-overlay" onMouseDown={onClose}>
      <div
        className="exercise-picker-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="exercise-picker-modal-header">
          <h3>{title}</h3>
          <button
            type="button"
            className="exercise-picker-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="exercise-picker-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search exercises..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="exercise-picker-results">
          {loading && <div className="exercise-picker-status">Searching...</div>}
          {!loading && results.length === 0 && query.length >= 2 && (
            <div className="exercise-picker-status">No exercises found</div>
          )}
          {!loading && query.length < 2 && (
            <div className="exercise-picker-status">
              Type at least 2 characters to search
            </div>
          )}
          {results.map((ex) => {
            const thumb = ex.image || getYouTubeThumbUrl(ex.video);
            return (
              <button
                key={ex._id || ex.exerciseId}
                type="button"
                className="exercise-picker-item"
                title={ex.name}
                onClick={() => onSelect?.(ex)}
              >
                <div className="exercise-picker-thumb">
                  {thumb ? <img src={thumb} alt="" /> : <FaDumbbell />}
                </div>
                <div className="exercise-picker-info">
                  <div className="exercise-picker-name">{ex.name}</div>
                  <div className="exercise-picker-meta">
                    {ex.category && <span>{ex.category}</span>}
                    {ex.difficulty && <span>{ex.difficulty}</span>}
                    {ex.measurementType === 'time' && <span>timed</span>}
                    {ex.targetMuscles?.length > 0 && (
                      <span>{ex.targetMuscles.slice(0, 2).join(', ')}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExerciseSearchPicker;
